import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TopNav, { SidebarIcon } from './layout/TopNav';
import logo from '../logo.svg';
import ReportIssueModal from './ReportIssueModal';
import { isBetaHelpEnabled } from '../betaHelp';
import { ReportIssueContext } from '../reportIssueContext';
import SessionExpiryGuard from './SessionExpiryGuard';
import useModalFocusTrap from './useModalFocusTrap';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sumrise.sidebarCollapsed';
const MOBILE_NAV_QUERY = '(max-width: 767px)';

function useMediaQuery(query) {
  const getMatches = () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia(query);
    const updateMatches = (event) => setMatches(event.matches);
    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', updateMatches);
    return () => mediaQuery.removeEventListener?.('change', updateMatches);
  }, [query]);

  return matches;
}

function AuthenticatedLayout({ children }) {
  const location = useLocation();
  const isMobile = useMediaQuery(MOBILE_NAV_QUERY);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  });
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);
  const [reportIssueContext, setReportIssueContext] = useState({});

  useModalFocusTrap({
    active: isDrawerOpen,
    containerRef: drawerRef,
    restoreFocusRef: menuButtonRef,
  });

  useEffect(() => {
    setIsReportIssueOpen(false);
    setIsDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isMobile) setIsDrawerOpen(false);
  }, [isMobile]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(isSidebarCollapsed));
    } catch (_error) {
      // Ignore localStorage write issues and keep the UI usable.
    }
  }, [isSidebarCollapsed]);

  return (
    <ReportIssueContext.Provider value={{ setReportIssueContext }}>
      <div className={`authed-layout${isSidebarCollapsed ? ' is-sidebar-collapsed' : ''}`}>
        {isMobile ? (
          <>
            <header className="mobile-app-bar">
              <button
                ref={menuButtonRef}
                type="button"
                className="mobile-menu-button sidebar-icon-box"
                aria-label="Open navigation"
                aria-expanded={isDrawerOpen}
                aria-controls="mobile-navigation-drawer"
                onClick={() => setIsDrawerOpen(true)}
              >
                <SidebarIcon name="menu" width="18" height="18" />
              </button>
              <span className="mobile-app-brand"><img src={logo} alt="" />Sumrise</span>
            </header>
            {isDrawerOpen ? (
              <div className="mobile-drawer-layer">
                <button className="mobile-drawer-backdrop" aria-label="Close navigation" onClick={() => setIsDrawerOpen(false)} />
                <div ref={drawerRef} id="mobile-navigation-drawer" className="mobile-drawer" role="dialog" aria-modal="true" aria-label="Navigation">
                  <button type="button" className="mobile-drawer-close sidebar-icon-box" aria-label="Close navigation" onClick={() => setIsDrawerOpen(false)}>×</button>
                  <TopNav
                    mobile
                    onNavigate={() => setIsDrawerOpen(false)}
                    onReportIssue={isBetaHelpEnabled() ? () => { setIsDrawerOpen(false); setIsReportIssueOpen(true); } : null}
                  />
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <TopNav
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
            onReportIssue={isBetaHelpEnabled() ? () => setIsReportIssueOpen(true) : null}
          />
        )}
        <main className="app-main app-main--viewport-fill">{children}</main>
        <SessionExpiryGuard />
        {isBetaHelpEnabled() && isReportIssueOpen ? (
          <ReportIssueModal
            context={reportIssueContext}
            onClose={() => setIsReportIssueOpen(false)}
          />
        ) : null}
      </div>
    </ReportIssueContext.Provider>
  );
}

export default AuthenticatedLayout;
